import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { SectionLabel } from '@/components/ui/section-label';
import { cn } from '@/lib/utils';
import { useDialogStream } from '@/hooks/use-dialog-stream';
import { useDialogBatchSubmit } from '@/hooks/use-dialog';
import { useRefocusAfterStreaming } from '@/hooks/use-refocus-after-streaming';
import { track } from '@/lib/analytics';
import { nodeTypeSurface } from '@/lib/node-visuals';
import { DRAFT_TYPE_CONFIG } from './draft-item-config';
import { Send, Loader2, ChevronDown, ChevronUp, Link, ClipboardCheck, CheckCircle2, RefreshCcw, X } from 'lucide-react';
import type { DialogPublicInfo, DialogSessionState, DraftItem, DraftItemStatus, PersistedDraftItem, PersistedMessage } from '@/types/dialog';
import type { ExtractedItem } from '@/types/api';
import { DialogReview } from './DialogReview';

type DialogMode = 'chat' | 'review' | 'complete';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  // True for turns that tripped the Bedrock Guardrail. Shown in the UI so the
  // respondent sees what happened, but excluded from conversation history
  // sent to the model — otherwise the guardrail keeps re-firing on the stale
  // adversarial content.
  excludeFromHistory?: boolean;
}

interface DialogChatProps {
  token: string;
  dialogInfo: DialogPublicInfo;
  streamUrl: string | null;
  // Stable anonymous actor id, owned by the page so the same value flows
  // to both the GET (via useDialogSession) and the streaming send. May be
  // undefined for the empty-token state before the route param resolves.
  clientSessionId?: string;
}

function DraftPanel({ items, onReview, disabled, suggested }: { items: DraftItem[]; onReview: () => void; disabled?: boolean; suggested?: boolean }) {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  const grouped = {
    job: items.filter((i) => i.type === 'job'),
    challenge: items.filter((i) => i.type === 'challenge'),
    opportunity: items.filter((i) => i.type === 'opportunity'),
  };

  return (
    <div className="border-t bg-muted/30">
      <div className="px-4 py-2 flex items-center gap-2 text-sm text-muted-foreground">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex-1 flex items-center justify-between hover:opacity-70 transition-opacity"
        >
          <span className="font-medium">
            {items.length} insight{items.length !== 1 ? 's' : ''} captured
            <span className="font-normal"> so far</span>
          </span>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <Button
          size="sm"
          variant="default"
          onClick={onReview}
          disabled={disabled}
          className={`shrink-0 h-7 ${suggested ? 'ring-2 ring-primary ring-offset-2 ring-offset-background motion-safe:animate-pulse' : ''}`}
        >
          <ClipboardCheck className="h-3 w-3 mr-1" />
          {suggested ? 'Review & submit →' : 'Review & submit'}
        </Button>
      </div>
      <p className="px-4 pb-2 text-xs text-muted-foreground">
        These reach the team once you review and submit them.
      </p>
      {expanded && (
        <div className="px-4 pb-3 space-y-3 max-h-60 overflow-y-auto">
          {(['job', 'challenge', 'opportunity'] as const).map((type) => {
            const group = grouped[type];
            if (group.length === 0) return null;
            const config = DRAFT_TYPE_CONFIG[type];
            return (
              <div key={type} className="space-y-1.5">
                <SectionLabel>{config.label}s ({group.length})</SectionLabel>
                {group.map((item) => {
                  const Icon = config.icon;
                  // Resolve titles only against jobs — parentJobIds should never
                  // reference non-jobs but if a stale id slips through we don't
                  // want to show a misleading "linked to" pointing at, say,
                  // another challenge.
                  const parentTitles = item.parentJobIds
                    .map((id) => grouped.job.find((j) => j.id === id)?.title)
                    .filter((t): t is string => Boolean(t));
                  const isNew = Date.now() - new Date(item.extractedAt).getTime() < 3000;
                  return (
                    <div
                      key={item.id}
                      className={cn(
                        'flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-all',
                        isNew && 'ring-2 ring-primary/30 animate-in fade-in duration-500',
                      )}
                      style={nodeTypeSurface(config.hue)}
                    >
                      <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium leading-tight">{item.title}</p>
                        {parentTitles.length > 0 && (
                          <p className="flex items-center gap-1 mt-0.5 opacity-70">
                            <Link className="h-2.5 w-2.5 shrink-0" />
                            <span className="line-clamp-2">{formatLinkedTitles(parentTitles)}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function DialogChat({ token, dialogInfo, streamUrl, clientSessionId }: DialogChatProps) {
  const persistedState = dialogInfo.state && 'messages' in dialogInfo.state
    ? (dialogInfo.state as DialogSessionState)
    : null;
  const restoreError = dialogInfo.state && 'error' in dialogInfo.state
    ? dialogInfo.state.error
    : null;
  const headerFocus = dialogInfo.respondentFraming;

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    // Welcome stays on top whether we're starting fresh or hydrating. From
    // the user's perspective it's part of how the conversation began —
    // re-reading the chat after a refresh, the opener anchors the framing
    // (e.g. "the AI was focused on Pre-seed/Seed") rather than forcing them
    // to infer it from the AI's questions alone. Safe to keep visible on
    // reload because the model's view of history now comes from AgentCore
    // Memory (server-side), and the welcome was never appended to a Memory
    // event — so it can't double-up the prompt.
    // Note: regenerated from current dialogInfo, so if the PM edits respondent
    // context mid-conversation the reload shows the new context's welcome,
    // not the historical one — acceptable minor inconsistency.
    const welcome: ChatMessage = {
      id: 'welcome',
      role: 'assistant',
      content: dialogInfo.welcomeMessage,
      timestamp: new Date(),
    };
    return persistedState && persistedState.messages.length > 0
      ? [welcome, ...hydrateMessages(persistedState.messages)]
      : [welcome];
  });
  const [input, setInput] = useState('');
  const [draftItems, setDraftItems] = useState<DraftItem[]>(() =>
    persistedState ? hydrateDraftItems(persistedState.draftItems) : [],
  );
  const [mode, setMode] = useState<DialogMode>('chat');
  const [restoreBannerDismissed, setRestoreBannerDismissed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Stable per-dialog id for anonymous respondents (server uses it as the
  // AgentCore Memory actorId). Persisted in localStorage so a refresh
  // doesn't re-roll the id and orphan the persisted state. Invited
  // respondents key on their respondent.id server-side and ignore this,
  // but we still pass it so LangSmith threading remains consistent.
  // clientSessionId comes from the page (Dialog.tsx) as a prop, so the GET
  // and the stream send agree on the actor id even in localStorage-blocked
  // browsers. The page also keys this component on `token`, so a token
  // change remounts and we never read a stale value from a prior dialog.

  const stream = useDialogStream(token, streamUrl);
  useRefocusAfterStreaming(inputRef, stream.isStreaming);
  const batchSubmit = useDialogBatchSubmit(token);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Latest assistant turn's wrap-up hint. Cleared when the respondent sends a
  // new message so a stale signal doesn't pulse the Review button while the
  // next turn is in flight.
  const [wrapUpSuggested, setWrapUpSuggested] = useState(false);
  // Per-mount guard so a long conversation reports starting once. A resumed
  // conversation fires it again, which is harmless: the funnel counts distinct
  // sessionIds, so dedup happens at read time rather than needing another
  // localStorage flag in a component already coordinating several.
  const startedTracked = useRef(false);

  // When streaming completes, add the AI response and merge extracted items.
  // On guardrail intervention, tag the AI reply AND the last user message as
  // excluded from history so the stale adversarial content doesn't keep
  // re-triggering the guardrail on future turns.
  useEffect(() => {
    if (stream.result) {
      const intervened = stream.result.intervention === true;
      const data = stream.result.data;
      const extractedItems = data.extractedItems ?? [];
      // Don't latch wrap-up on a blocked turn — model output is untrusted.
      setWrapUpSuggested(!intervened && data.shouldWrapUp === true);
      setMessages((prev) => {
        const withoutStreaming = prev.filter((m) => m.id !== 'streaming');
        const next = intervened
          ? withoutStreaming.map((m, i, arr) =>
              i === arr.length - 1 && m.role === 'user' ? { ...m, excludeFromHistory: true } : m,
            )
          : withoutStreaming;
        return [...next, {
          id: `ai-${Date.now()}`,
          role: 'assistant',
          content: data.reply,
          timestamp: new Date(),
          excludeFromHistory: intervened,
        }];
      });

      // Belt-and-braces: the Lambda already strips extractedItems on
      // intervention, but never merge anything from a blocked turn even if
      // a future change accidentally drops that guarantee.
      if (!intervened && extractedItems.length > 0) {
        setDraftItems((prev) => mergeDraftItems(prev, extractedItems));
      }
    }
  }, [stream.result]);

  // Handle streaming errors
  useEffect(() => {
    if (stream.error) {
      setMessages((prev) => {
        const withoutStreaming = prev.filter((m) => m.id !== 'streaming');
        return [...withoutStreaming, {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: stream.error!,
          timestamp: new Date(),
        }];
      });
    }
  }, [stream.error]);

  // Auto-scroll to bottom
  useEffect(() => {
    const behavior: ScrollBehavior = stream.isStreaming ? 'auto' : 'smooth';
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, [messages, stream.streamingText, stream.isStreaming]);

  // When returning to chat from review, jump instantly to the bottom so the
  // respondent lands on the most recent messages rather than the top of the
  // conversation. 'auto' is intentional — 'smooth' from the top of a long
  // conversation is jarring.
  useEffect(() => {
    if (mode === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [mode]);

  const handleSend = () => {
    if (!input.trim() || stream.isStreaming) return;

    const userMessage = input.trim();
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';

    setMessages((prev) => [...prev, {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
    }]);
    setWrapUpSuggested(false);

    if (!startedTracked.current) {
      startedTracked.current = true;
      track('dialog_started', { sessionId: clientSessionId });
    }

    // Conversation history and draft items are no longer sent on the wire —
    // the server reads them from AgentCore Memory keyed on (clientSessionId,
    // dialogId). All we send is the new message and the actor id.
    stream.sendMessage({
      message: userMessage,
      clientSessionId,
    });

    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value.slice(0, 2000));
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
  };

  // Review-mode handlers
  const handleItemUpdate = (id: string, updates: Partial<DraftItem>) => {
    setDraftItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...updates } : i)));
  };
  const handleItemReject = (id: string) => {
    setDraftItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, status: 'rejected' as const } : i)),
    );
  };
  const handleItemConfirm = (id: string) => {
    setDraftItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        // Rejected is terminal — ignore clicks on rejected items.
        // (In practice they aren't rendered in the review screen anyway.)
        if (i.status === 'rejected') return i;
        // Toggle: pending → confirmed, confirmed → pending.
        const next: DraftItemStatus = i.status === 'confirmed' ? 'pending' : 'confirmed';
        return { ...i, status: next };
      }),
    );
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const confirmed = draftItems.filter((i) => i.status === 'confirmed');
    if (confirmed.length === 0) return;
    // Only forward parent ids that point at confirmed jobs. Anything pointing
    // at a non-job (semantically invalid) or at a non-confirmed item (rejected
    // or unchecked) gets dropped here so the backend doesn't have to silently
    // discard half-valid edges.
    const confirmedJobIds = new Set(
      confirmed.filter((i) => i.type === 'job').map((i) => i.id),
    );
    try {
      await batchSubmit.mutateAsync({
        // Identifies this anonymous session so distinct anonymous respondents
        // count separately in synthesis (invited respondents key on email).
        clientSessionId,
        items: confirmed.map((i) => ({
          id: i.id,
          type: i.type,
          title: i.title,
          description: i.description,
          parentJobIds: i.parentJobIds.filter((id) => confirmedJobIds.has(id)),
          edited: i.edited,
        })),
      });
      // After the await, so a failed submit never counts as a completion.
      track('dialog_completed', { sessionId: clientSessionId, itemCount: confirmed.length });
      setMode('complete');
    } catch (err) {
      setSubmitError((err as Error).message || "That didn't go through. Please try again.");
    }
  };

  // Shared by the DraftPanel button and the in-stream CTA below — same
  // tracked event and mode transition regardless of which affordance the
  // respondent uses to reach review.
  const goToReview = () => {
    track('dialog_reached_review', { sessionId: clientSessionId });
    setMode('review');
  };

  if (mode === 'review') {
    return (
      <DialogReview
        items={draftItems}
        onUpdate={handleItemUpdate}
        onReject={handleItemReject}
        onConfirm={handleItemConfirm}
        onBackToChat={() => setMode('chat')}
        onSubmit={handleSubmit}
        submitting={batchSubmit.isPending}
        submitError={submitError}
      />
    );
  }

  if (mode === 'complete') {
    const finalCount = draftItems.filter((i) => i.status === 'confirmed').length;
    return (
      <div className="min-h-screen bg-linear-to-b from-background to-muted flex items-center justify-center px-4">
        <div className="w-full max-w-md p-8 bg-card rounded-xl shadow-lg border text-center space-y-4">
          <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
          <h1 className="text-xl font-semibold">Thanks, your insights are captured</h1>
          <p className="text-sm text-muted-foreground">
            You shared {finalCount} insight{finalCount !== 1 ? 's' : ''} with the team.
            They'll review them shortly.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-linear-to-b from-background to-muted py-4 px-4 sm:py-8">
      <div className="max-w-2xl mx-auto flex flex-col h-[calc(100vh-2rem)] sm:h-[calc(100vh-4rem)] bg-card rounded-xl shadow-lg border overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b bg-card/80 backdrop-blur-sm">
          <h1 className="text-lg font-semibold">Insight conversation</h1>
          {headerFocus && (
            <p className="text-sm text-muted-foreground mt-0.5">Focused on: {headerFocus}</p>
          )}
        </div>

        {/* How-it-works + privacy strip: always visible. Covers the cold-start
            gap before the draft panel first renders (it only appears after the
            first extraction), and carries the respondent-facing data notice. */}
        <div className="px-4 py-2 border-b bg-muted/40 text-xs text-muted-foreground">
          Chat naturally. Insights are captured as you go, and at the end you review and
          confirm what goes into the research.{' '}
          <a href={`${import.meta.env.BASE_URL}privacy`} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
            Privacy details
          </a>
        </div>

        {/* Restore-failure banner: surfaces when the server flagged the
            persisted state as unloadable (transient API error or a
            dialogVersion mismatch from a prompt iteration). Treated as
            recoverable — the conversation continues fresh. Dismissable so
            it doesn't linger once the respondent has acknowledged it. */}
        {restoreError && !restoreBannerDismissed && (
          <div className="flex items-start gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
            <RefreshCcw className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
            <p className="flex-1">
              {restoreError === 'version_mismatch'
                ? "We've updated how this conversation is structured since your last visit, so we're starting fresh."
                : "We couldn't restore your previous conversation, so we're starting fresh."}
            </p>
            <button
              type="button"
              onClick={() => setRestoreBannerDismissed(true)}
              className="shrink-0 p-0.5 -m-0.5 hover:bg-amber-100 rounded"
              aria-label="Dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
          {messages.map((msg) => (
            <div key={msg.id} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
              <div className={cn(
                'max-w-[80%] rounded-2xl px-4 py-3 text-sm',
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground rounded-br-md'
                  : 'bg-muted rounded-bl-md',
              )}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}

          {/* Streaming indicator */}
          {stream.isStreaming && (
            <div className="flex justify-start">
              <div className="max-w-[80%] rounded-2xl rounded-bl-md px-4 py-3 text-sm bg-muted">
                <p className="whitespace-pre-wrap">
                  {stream.streamingText || '...'}
                  <span className="inline-block w-1.5 h-4 bg-current ml-0.5 animate-pulse" />
                </p>
              </div>
            </div>
          )}

          {/* In-stream wrap-up CTA. The system prompt tells the model a matching
              button appears under its reply, so this must render whenever the
              wrap-up hint is latched — otherwise the model describes an affordance
              that isn't there. Same action as the strip button. */}
          {wrapUpSuggested && !stream.isStreaming && draftItems.some((i) => i.status !== 'rejected') && (
            <div className="flex justify-start">
              <Button variant="brand" size="sm" onClick={goToReview}>
                Review & submit
              </Button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Draft panel */}
        <DraftPanel
          items={draftItems.filter((i) => i.status !== 'rejected')}
          onReview={goToReview}
          disabled={stream.isStreaming}
          suggested={wrapUpSuggested}
        />

        {/* Input */}
        <div className="px-4 py-4 border-t bg-card">
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              rows={1}
              className="flex-1 px-4 py-2 border rounded-xl bg-background text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/50 resize-none overflow-y-auto"
              style={{ maxHeight: '160px' }}
              disabled={stream.isStreaming}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || stream.isStreaming}
              size="icon"
              className="rounded-xl shrink-0"
            >
              {stream.isStreaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className={cn('text-xs text-right mt-1', input.length > 1800 ? 'text-amber-600' : 'text-muted-foreground')}>
            {input.length > 0 ? `${input.length.toLocaleString()} / 2,000` : ''}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Convert the server-persisted message list into the local ChatMessage shape.
 *  Intervention messages are tagged excludeFromHistory so they render in the
 *  chat but skip the next turn's model context — mirroring the live-session
 *  behaviour for guardrail-tripped turns. Timestamps fall back to "now" since
 *  AgentCore Memory's event timestamp isn't per-message granular and the UI
 *  doesn't use it visibly. */
function hydrateMessages(persisted: PersistedMessage[]): ChatMessage[] {
  const now = new Date();
  return persisted.map((m, i) => ({
    id: `hydrated-${i}`,
    role: m.role,
    content: m.content,
    timestamp: now,
    ...(m.intervention ? { excludeFromHistory: true } : {}),
  }));
}

/** Convert server-persisted draft items into the richer local DraftItem shape
 *  expected by the review flow. extractedAt is intentionally stale (epoch) so
 *  the DraftPanel's "isNew" pulse animation (gated on `now - extractedAt <
 *  3000ms`) doesn't fire for every restored item on reload. Status defaults
 *  to pending — the respondent's confirm/reject state isn't persisted on the
 *  server today (only the cumulative captured snapshot), so on rehydration
 *  every item lands as pending and the respondent reviews afresh. */
function hydrateDraftItems(persisted: PersistedDraftItem[]): DraftItem[] {
  const epoch = new Date(0).toISOString();
  return persisted.map((item) => ({
    ...item,
    extractedAt: epoch,
    status: item.status ?? 'pending',
  }));
}

/** Compact human-readable summary of linked job titles. Truncates very long
 *  titles per item so a chain of three full-length JTBD titles doesn't push the
 *  line off the panel. Empty input is the caller's responsibility. */
function formatLinkedTitles(titles: string[]): string {
  const cap = 50;
  const truncate = (t: string) => (t.length > cap ? t.slice(0, cap) + '…' : t);
  if (titles.length === 1) return truncate(titles[0]);
  if (titles.length === 2) return `${truncate(titles[0])} and ${truncate(titles[1])}`;
  return `${truncate(titles[0])}, ${truncate(titles[1])}, and ${titles.length - 2} other${titles.length - 2 === 1 ? '' : 's'}`;
}

/** Title key for duplicate detection: case-insensitive, whitespace-collapsed.
 *  Mirrors the server-side mergeDraftItems guard (lambda/shared/dialog-ai.ts). */
function titleKey(title: string): string {
  return title.toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Merge newly extracted items into the draft. Updates existing items by ID —
 *  or by title when the model re-extracts an existing item under a fresh id,
 *  which the prompt forbids but still happens — and appends genuinely new ones. */
function mergeDraftItems(existing: DraftItem[], newItems: ExtractedItem[]): DraftItem[] {
  const result = [...existing];
  const now = new Date().toISOString();
  // Ids the model minted for deduped re-emits, mapped to the surviving draft
  // id — sibling items in the same batch may link to the minted id.
  const remapped = new Map<string, string>();
  // Pre-pass against the pre-batch draft so a sibling that references a
  // minted id BEFORE the duplicate appears in the batch still gets remapped
  // (the in-loop set below covers dups of items appended this same batch).
  for (const item of newItems) {
    if (!result.some((d) => d.id === item.id)) {
      const match = result.find((d) => titleKey(d.title) === titleKey(item.title));
      if (match) remapped.set(item.id, match.id);
    }
  }

  for (const item of newItems) {
    const parentJobIds = item.parentJobIds.map((id) => remapped.get(id) ?? id);
    let idx = result.findIndex((d) => d.id === item.id);
    if (idx < 0) {
      idx = result.findIndex((d) => titleKey(d.title) === titleKey(item.title));
      if (idx >= 0) remapped.set(item.id, result[idx].id);
    }
    if (idx >= 0) {
      const current = result[idx];
      const updated: DraftItem = {
        ...current,
        type: item.type,
        extractedAt: now,
      };
      // If the respondent edited this item, preserve their text. The model may
      // still re-emit the item to update the link (parentJobIds) — which we allow
      // — but title and description stay as the respondent wrote them.
      if (!current.edited) {
        updated.title = item.title;
        if (item.description !== undefined) updated.description = item.description;
      }
      // Gated by current.edited for the same reason as title/description: if
      // the respondent edited links in review (added or removed parents) and
      // then kept chatting, a later model re-emit shouldn't silently undo
      // their choices. The model only wins on link updates for items the
      // respondent hasn't touched yet.
      if (!current.edited) {
        updated.parentJobIds = parentJobIds;
      }
      result[idx] = updated;
    } else {
      result.push({ ...item, parentJobIds, extractedAt: now, status: 'pending' });
    }
  }

  return result;
}
